import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
    Edit as EditIcon, 
    DeleteForever as DeleteIcon 
} from '@mui/icons-material';
import AdminArticleForm from './AdminArticleForm';
import AdminImagePreviewModal from './AdminImagePreviewModal';
import AdminAiPromptModal from './AdminAiPromptModal'; // Import the new AI modal

const AdminArticleManager = () => {
    const [articles, setArticles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // State for the main Article Form modal
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingArticleId, setEditingArticleId] = useState(null);
    const [aiGeneratedData, setAiGeneratedData] = useState(null);

    // State for the AI Prompt modal
    const [isAiModalOpen, setIsAiModalOpen] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    
    // State for the image preview modal
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [previewImageUrl, setPreviewImageUrl] = useState('');

    const [aiArticleOptions, setAiArticleOptions] = useState([]);
    const [isOptionModalOpen, setIsOptionModalOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);
    const [fromAiOptions, setFromAiOptions] = useState(false); // Track if form was opened from AI options
    
    // State for viewing full article content
    const [isViewFullModalOpen, setIsViewFullModalOpen] = useState(false);
    const [viewingArticle, setViewingArticle] = useState(null);

    // Add state for category filter
    const [categoryFilter, setCategoryFilter] = useState('All');

    const fetchArticles = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('app_token');
            const response = await axios.get('/api/admin/articles', {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log('Articles loaded:', response.data);
            setArticles(response.data);
            setError(null);
        } catch (err) {
            console.error('Error loading articles:', err);
            setError("Failed to load articles.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchArticles();
    }, []);

    const handleOpenFormForEdit = (articleId) => {
        setEditingArticleId(articleId);
        setAiGeneratedData(null); // Clear any AI data
        setIsFormOpen(true);
    };

    const handleOpenFormForNew = () => {
        setEditingArticleId(null);
        setAiGeneratedData(null);
        setIsFormOpen(true);
    };

    const handleCloseForm = () => {
        setIsFormOpen(false);
        setEditingArticleId(null);
        setAiGeneratedData(null);
    };

    const handleSave = async (formData) => {
        const token = localStorage.getItem('app_token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        
        if (editingArticleId) {
            await axios.put(`/api/admin/articles/${editingArticleId}`, formData, config);
        } else {
            await axios.post(`/api/admin/articles`, formData, config);
        }
        fetchArticles();
        handleCloseForm();
    };

    const handleDelete = async (articleId) => {
        if (window.confirm('Are you sure you want to delete this article?')) {
            try {
                const token = localStorage.getItem('app_token');
                await axios.delete(`/api/admin/articles/${articleId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                fetchArticles();
            } catch (err) {
                alert('Error deleting article.');
            }
        }
    };

    const handleStatusToggle = async (id, currentStatus) => {
        try {
            const token = localStorage.getItem('app_token');
            const newStatus = !currentStatus;
            await axios.put(`/api/admin/articles/${id}/status`, 
                { isActive: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setArticles(articles.map(article => 
                article.id === id ? { ...article, is_active: newStatus } : article
            ));
        } catch (err) {
            alert('Failed to update status.');
        }
    };

    const handleGenerateWithAi = async (summary, provider) => {
        setIsGenerating(true);
        try {
            const token = localStorage.getItem('app_token');
            const response = await axios.post('/api/admin/articles/generate-with-ai', 
                { summary, provider },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (Array.isArray(response.data.options) && response.data.options.length > 0) {
                setAiArticleOptions(response.data.options);
                setIsOptionModalOpen(true);
            } else {
                setAiGeneratedData(response.data);
                setIsFormOpen(true);
            }
            setIsAiModalOpen(false);
            setEditingArticleId(null);
        } catch (error) {
            alert("AI generation failed. Please check the backend logs or your API key.");
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSelectArticleOption = (option) => {
        setAiGeneratedData({
            title: option.title,
            summary: option.summary,
            content: option.articleContent,
            category: '',
            tags: option.tags,
        });
        setIsFormOpen(true);
        setIsOptionModalOpen(false);
        setFromAiOptions(true);
        setSelectedOption(option);
    };

    // Handler to go back to options from the form
    const handleBackToOptions = () => {
        setIsFormOpen(false);
        setIsOptionModalOpen(true);
        setFromAiOptions(false);
    };

    const handleOpenPreview = (imageUrl) => {
        setPreviewImageUrl(imageUrl);
        setIsPreviewModalOpen(true);
    };

    const handleViewFullArticle = (option, e) => {
        e.stopPropagation(); // Prevent triggering the select function
        setViewingArticle(option);
        setIsViewFullModalOpen(true);
    };

    // Compute unique categories from articles
    const categories = Array.from(new Set(articles.map(a => a.category).filter(Boolean)));

    // Filtered articles by search term and category
    const filteredArticles = articles.filter(article =>
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
        (categoryFilter === 'All' || article.category === categoryFilter)
    );

    if (isLoading) return <p>Loading articles...</p>;
    if (error) return <p className="error-message">{error}</p>;

    return (
        <div className="admin-content-manager">
            {/* Search by article name only */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
                <input
                    type="text"
                    placeholder="Search by article name..."
                    className="search-input"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    style={{ padding: '6px 12px', borderRadius: 4, flex: 1 }}
                />
                <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} style={{ padding: '6px 12px', borderRadius: 4, minWidth: 120 }}>
                    <option value="All">All Categories</option>
                    {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                    ))}
                </select>
            </div>
            {isAiModalOpen && (
                <AdminAiPromptModal 
                    onGenerate={handleGenerateWithAi}
                    onCancel={() => setIsAiModalOpen(false)}
                    isGenerating={isGenerating}
                />
            )}
            {isPreviewModalOpen && (
                <AdminImagePreviewModal imageUrl={previewImageUrl} onCancel={() => setPreviewImageUrl('')} />
            )}
            {isFormOpen && (
                <AdminArticleForm 
                    articleId={editingArticleId} 
                    initialData={aiGeneratedData} 
                    onSave={handleSave} 
                    onCancel={handleCloseForm} 
                    onBackToOptions={fromAiOptions ? handleBackToOptions : undefined}
                />
            )}

            {isOptionModalOpen && (
                <div className="modal-overlay">
                    <div className="modal floating-modal" style={{ maxHeight: '90vh', overflowY: 'auto', minWidth: 500 }}>
                        <h3>Select an AI-Generated Article Option</h3>
                        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                            {aiArticleOptions.map((option, idx) => (
                                <div key={idx} className="ai-article-option" style={{ border: '1px solid #ccc', borderRadius: 8, padding: 12, marginBottom: 12, cursor: 'pointer', background: selectedOption === option ? '#f0f8ff' : '#fff' }} onClick={() => handleSelectArticleOption(option)}>
                                    <strong>{option.title}</strong>
                                    <div style={{ margin: '8px 0', color: '#666', fontSize: 14 }}>{option.summary}</div>
                                    <div style={{ fontSize: 13, color: '#888', marginBottom: 6 }}><b>Tags:</b> {option.tags}</div>
                                    <div style={{ maxHeight: 100, overflow: 'auto', fontSize: 13, border: '1px solid #eee', padding: 6, background: '#fafbfc' }} dangerouslySetInnerHTML={{ __html: option.articleContent?.slice(0, 600) + (option.articleContent?.length > 600 ? '...' : '') }} />
                                    <button 
                                        onClick={(e) => handleViewFullArticle(option, e)}
                                        style={{ 
                                            marginTop: 8, 
                                            padding: '4px 8px', 
                                            fontSize: 12, 
                                            background: '#f0f8ff', 
                                            border: '1px solid #2563eb', 
                                            color: '#2563eb', 
                                            borderRadius: 4, 
                                            cursor: 'pointer' 
                                        }}
                                    >
                                        View Full Article
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button className="btn-secondary" onClick={() => setIsOptionModalOpen(false)} style={{ marginTop: 16 }}>Cancel</button>
                    </div>
                </div>
            )}

            {isViewFullModalOpen && viewingArticle && (
                <div className="modal-overlay">
                    <div className="modal floating-modal" style={{ maxHeight: '90vh', overflowY: 'auto', minWidth: 600, maxWidth: '80vw' }}>
                        <h3>{viewingArticle.title}</h3>
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 14, color: '#666', marginBottom: 8 }}><strong>Summary:</strong></div>
                            <div style={{ fontSize: 14, color: '#333', marginBottom: 12 }}>{viewingArticle.summary}</div>
                            <div style={{ fontSize: 13, color: '#888' }}><strong>Tags:</strong> {viewingArticle.tags}</div>
                        </div>
                        <div style={{ 
                            maxHeight: '60vh', 
                            overflowY: 'auto', 
                            border: '1px solid #eee', 
                            padding: 16, 
                            background: '#fafbfc',
                            borderRadius: 8,
                            lineHeight: 1.6
                        }} 
                        dangerouslySetInnerHTML={{ __html: viewingArticle.articleContent }} 
                        />
                        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                            <button 
                                className="btn-primary" 
                                onClick={() => {
                                    setIsViewFullModalOpen(false);
                                    handleSelectArticleOption(viewingArticle);
                                }}
                            >
                                Select This Article
                            </button>
                            <button className="btn-secondary" onClick={() => setIsViewFullModalOpen(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="admin-content-header">
                <h3 className="admin-section-title">Manage Articles</h3>
                <div style={{ display: 'flex', gap: '1rem' }}>
                    <button onClick={() => setIsAiModalOpen(true)} className="btn-primary" style={{backgroundColor: '#6c47ff'}}>Create with AI</button>
                    <button onClick={handleOpenFormForNew} className="btn-secondary">Write New Article</button>
                </div>
            </div>
            
            <div className="admin-table-container">
                {filteredArticles.length === 0 ? (
                    <p>No articles found.</p>
                ) : (
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Banner</th>
                                <th>Title</th>
                                <th>Category</th>
                                <th>Status</th>
                                <th className="admin-actions-column" style={{ minWidth: 80, textAlign: 'center' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredArticles.map(article => (
                                <tr key={article.id} style={{ opacity: article.is_active ? 1 : 0.5 }}>
                                    <td>{article.id}</td>
                                    <td>
                                        <div onClick={() => handleOpenPreview(article.banner_image_url)} style={{ cursor: 'pointer' }}>
                                            <img 
                                              src={article.banner_image_url || 'https://placehold.co/300x180?text=No+Image'}
                                              alt="Banner" 
                                              className="admin-table-thumbnail"
                                              onError={e => { e.target.onerror = null; e.target.src = 'https://placehold.co/300x180?text=No+Image'; }}
                                            />
                                        </div>
                                    </td>
                                    <td><Link to={`/articles/${article.id}`} target="_blank" rel="noopener noreferrer">{article.title}</Link></td>
                                    <td>{article.category || '-'}</td>
                                    <td>
                                        <div className="status-cell">
                                            <span className={`status-badge ${article.is_active ? 'active' : 'inactive'}`}>
                                                {article.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                            <label className="toggle-switch">
                                                <input type="checkbox" checked={article.is_active} onChange={() => handleStatusToggle(article.id, article.is_active)} />
                                                <span className="toggle-slider"></span>
                                            </label>
                                        </div>
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        <div className="action-buttons-container" style={{ justifyContent: 'center', gap: '8px' }}>
                                            <button 
                                                onClick={() => handleOpenFormForEdit(article.id)} 
                                                className="action-btn edit" 
                                                title="Edit"
                                                style={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'center', 
                                                    minWidth: '36px', 
                                                    minHeight: '36px',
                                                    border: 'none',
                                                    background: 'none',
                                                    cursor: 'pointer',
                                                    borderRadius: '6px',
                                                    transition: 'background-color 0.2s'
                                                }}
                                            >
                                                <EditIcon style={{ fontSize: '1.25rem', color: '#2563eb' }} />
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(article.id)} 
                                                className="action-btn delete" 
                                                title="Delete"
                                                style={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'center', 
                                                    minWidth: '36px', 
                                                    minHeight: '36px',
                                                    border: 'none',
                                                    background: 'none',
                                                    cursor: 'pointer',
                                                    borderRadius: '6px',
                                                    transition: 'background-color 0.2s'
                                                }}
                                            >
                                                <DeleteIcon style={{ fontSize: '1.25rem', color: '#dc2626' }} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default AdminArticleManager;
